import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { BrBox } from "@/components/BrBox";
import { BrBtn } from "@/components/BrBtn";
import { BrLabel } from "@/components/BrLabel";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/auth/AuthProvider";
import { mapFirebaseError } from "@/auth/firebase";
import { useToast } from "@/components/ToastProvider";
import { offsetShadow, TYPE, apiFetch, type UserOut } from "@chess/shared";
import { useTheme } from "@/theme/ThemeProvider";
import { useViewport } from "@/util/viewport";

type Tab = "login" | "signup";

export function LoginPage() {
  const p = useTheme();
  const { isMobile } = useViewport();
  const nav = useNavigate();
  const auth = useAuth();
  const toast = useToast();

  const [tab, setTab] = useState<Tab>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function syncSession() {
    const me = await apiFetch<UserOut>("/v1/auth/session", { method: "POST", body: "{}" });
    if (me.connected_accounts.length === 0) nav("/connect");
    else nav("/app");
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (tab === "signup") {
      if (password.length < 6) {
        setError("Password must be at least 6 characters.");
        return;
      }
      if (password !== confirm) {
        setError("Passwords don't match.");
        return;
      }
    }
    setBusy(true);
    try {
      if (tab === "login") await auth.signInEmail(email, password);
      else await auth.signUpEmail(email, password);
      await syncSession();
    } catch (err) {
      setError(mapFirebaseError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    setError(null);
    setBusy(true);
    try {
      await auth.signInGoogle();
      await syncSession();
    } catch (err) {
      setError(mapFirebaseError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleForgot() {
    if (!email) {
      setError("Enter your email above first, then click Forgot password.");
      return;
    }
    try {
      await auth.sendPasswordReset(email);
      toast.push({ kind: "success", text: "Password reset email sent." });
    } catch (err) {
      setError(mapFirebaseError(err));
    }
  }

  const inputStyle = {
    width: "100%",
    background: p.bg,
    color: p.fg,
    border: `2px solid ${p.border}`,
    padding: "12px 14px",
    fontFamily: TYPE.body,
    fontSize: 14,
    fontWeight: 600,
    outline: "none",
  } as const;

  function TabBtn({ id, label }: { id: Tab; label: string }) {
    const active = tab === id;
    return (
      <button
        type="button"
        onClick={() => {
          setTab(id);
          setError(null);
        }}
        style={{
          flex: 1,
          background: active ? p.fg : p.card,
          color: active ? p.bg : p.fg,
          border: `2px solid ${p.border}`,
          borderRight: id === "login" ? "none" : `2px solid ${p.border}`,
          padding: "12px 16px",
          fontFamily: TYPE.body,
          fontWeight: 800,
          fontSize: 13,
          letterSpacing: 1,
          textTransform: "uppercase",
          cursor: "pointer",
          boxShadow: active ? offsetShadow(p, 3, 3) : "none",
        }}
      >
        {label}
      </button>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, fontFamily: TYPE.body, display: "grid", placeItems: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 440 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: isMobile ? 22 : 26, fontWeight: 800, letterSpacing: -0.5, marginBottom: 18 }}>
          <Logo size={36} />
          GAMBIT.
        </div>

        <div style={{ display: "flex", marginBottom: 14 }}>
          <TabBtn id="login" label="Log in" />
          <TabBtn id="signup" label="Sign up" />
        </div>

        <BrBox padding={isMobile ? 22 : 28} shadowSize={8} label={tab === "login" ? "WELCOME BACK" : "CREATE ACCOUNT"}>
          <h1
            style={{
              fontFamily: TYPE.display,
              fontSize: isMobile ? 28 : 36,
              fontWeight: 800,
              letterSpacing: -1.5,
              lineHeight: 1,
              margin: "8px 0 14px",
              textTransform: "uppercase",
            }}
          >
            {tab === "login" ? "Get to work." : "Drill your blunders."}
          </h1>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <BrLabel>EMAIL</BrLabel>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ ...inputStyle, marginTop: 6 }}
                placeholder="you@example.com"
                autoFocus
                autoComplete="email"
              />
            </div>
            <div>
              <BrLabel>PASSWORD</BrLabel>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ ...inputStyle, marginTop: 6 }}
                placeholder="••••••••"
                autoComplete={tab === "login" ? "current-password" : "new-password"}
              />
            </div>
            {tab === "signup" && (
              <div>
                <BrLabel>CONFIRM PASSWORD</BrLabel>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  style={{ ...inputStyle, marginTop: 6 }}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
            )}

            {error && (
              <div
                style={{
                  background: p.danger,
                  color: p.dangerFg,
                  padding: "10px 12px",
                  fontSize: 12,
                  border: `2px solid ${p.border}`,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {error}
              </div>
            )}

            <BrBtn type="submit" variant="primary" size="lg" full disabled={busy}>
              {busy ? "Working…" : tab === "login" ? "Continue →" : "Create account →"}
            </BrBtn>

            {tab === "login" && (
              <button
                type="button"
                onClick={handleForgot}
                style={{
                  background: "transparent",
                  border: "none",
                  color: p.fgMuted,
                  fontSize: 12,
                  fontFamily: TYPE.mono,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  cursor: "pointer",
                  marginTop: -4,
                  textAlign: "right",
                }}
              >
                Forgot password?
              </button>
            )}
          </form>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              margin: "18px 0",
              fontSize: 10,
              fontFamily: TYPE.mono,
              fontWeight: 700,
              color: p.fgMuted,
              letterSpacing: 1,
            }}
          >
            <span style={{ flex: 1, height: 2, background: p.border }} />
            OR
            <span style={{ flex: 1, height: 2, background: p.border }} />
          </div>

          <BrBtn variant="surface" full onClick={handleGoogle} disabled={busy}>
            Continue with Google
          </BrBtn>

          <div style={{ marginTop: 16, fontSize: 11, fontFamily: TYPE.mono, fontWeight: 700, color: p.fgMuted, textAlign: "center", letterSpacing: 0.5 }}>
            By signing in, you agree to drill your blunders.
          </div>
        </BrBox>

        <div style={{ textAlign: "center", marginTop: 14 }}>
          <BrBtn variant="ghost" size="sm" withShadow={false} onClick={() => nav("/")}>
            ← Back home
          </BrBtn>
        </div>
      </div>
    </div>
  );
}
